import {ReactNode, useCallback} from 'react'
import './page.scss'
import {Field, Form, Formik, FormikHelpers} from "formik";
import * as Yup from "yup";
import {useNavigate, useSearchParams} from "react-router-dom";
import FormikPasswordInput from "@/components/form-components/formik-password-input.tsx";
import {Button} from "@/components/ui/button.tsx";
import {motion} from "framer-motion";
import {buttonVariants, containerVariants} from "@/constants/animations.ts";
import {CommonService} from "@/services";
import {LOGIN_ROUTE} from "@/constants/RoutesConfig.tsx";
import {BackIcon} from "@/constants/ImageConfig.ts";

interface SigninValuesType {
    new_password: string;
    confirm_password: string;
}

const formValidationSchema = Yup.object({
    new_password: Yup.string()
        .min(8, "Password must be 8 characters")
        .max(20, "Password cannot exceed 20 characters")
        .required("Password is required"),
    confirm_password: Yup.string()
        .required('Confirm Password is required')
        .oneOf([Yup.ref('new_password'), ""], 'Passwords must match'),
});

const initialValues: SigninValuesType = {
    new_password: '',
    confirm_password: ''
}


const ResetPasswordScreen = () => {
    const [searchParams] = useSearchParams();
    const verificationCode = searchParams.get('verification_code');
    const navigate = useNavigate();

    const onSetPassword = useCallback((payload: any, {setSubmitting}: FormikHelpers<any>) => {
        setSubmitting(true)
        const inputPayload = {
            new_password: payload.new_password,
            verification_code: verificationCode
        }
        CommonService._account.resetPassword(inputPayload)
            .then(() => {
                setSubmitting(false)
                CommonService._alert.showToast('Password reset successfully', 'success')
                navigate(LOGIN_ROUTE)
            })
            .catch((error: any) => {
                setSubmitting(false)
                CommonService._alert.showToast(error?.detail?.error || 'Failed to reset password', 'error')
            })
    }, [navigate, verificationCode])

    return (
        <div className={"screen"}>
            <motion.div className={'auth-screen-title-wrapper'}>
                <div className={'auth-screen-title'}>
                    Reset Password
                </div>
                <motion.div
                    className="auth-screen-subtitle text-secondary-foreground mt-2 mb-4"
                    variants={containerVariants}
                >
                    Enter your new password and confirm it to reset your account access.
                </motion.div>
            </motion.div>
            <Formik initialValues={initialValues} validationSchema={formValidationSchema}
                    validateOnChange={true}
                    validateOnBlur={false}
                    enableReinitialize={true}
                    validateOnMount={true}
                    onSubmit={onSetPassword}>
                {({handleSubmit, isSubmitting}) => {
                    return (
                        <Form onSubmit={handleSubmit}>
                            <Field name={'new_password'}>
                                {(field: any) => (
                                    <FormikPasswordInput label={'New Password'} canToggle={true}
                                                         className={'full-width'}
                                                         placeholder={''}
                                                         formikField={field}
                                                         size={'medium'}
                                    />
                                )}
                            </Field>
                            <Field name={'confirm_password'}>
                                {(field: any) => (
                                    <FormikPasswordInput label={'Confirm Password'}
                                                         canToggle={true} className={'full-width'}
                                                         placeholder={''}
                                                         formikField={field}
                                                         size={'medium'}
                                    />
                                )}
                            </Field>
                            <motion.div variants={buttonVariants} whileHover="hover">
                                <Button loading={isSubmitting}
                                        type={'submit'}
                                        fullWidth={true}
                                        mixpanelEvent={{
                                            name: "reset_password_save_clicked",
                                            properties: {}
                                        }}
                                >
                                    Save Password
                                </Button>
                            </motion.div>
                            <motion.div variants={buttonVariants} whileHover="hover">
                                <Button
                                    fullWidth
                                    type={"button"}
                                    variant={"link"}
                                    onClick={() => navigate(LOGIN_ROUTE)}
                                    prefixIcon={<BackIcon/> as ReactNode}
                                    mixpanelEvent={{
                                        name: "reset_password_back_to_signin_clicked",
                                        properties: {}
                                    }}
                                >
                                    Back to Sign In
                                </Button>
                            </motion.div>
                        </Form>
                    ) as ReactNode
                }}
            </Formik>
        </div>
    )
}

export default ResetPasswordScreen;
